import React, { useEffect, useState } from "react";
import {
  Typography,
  makeStyles,
  Box,
  Container,
  Paper,
  Zoom,
  Hidden,
} from "@material-ui/core";
import WIMMEL from "../assets/wimmelbild.jpg";
import CSV from "../assets/content_wimmelbild.csv";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import useWindowSize from "../hooks/useWindowSize";
import clsx from "clsx";
import { amber, red } from "@material-ui/core/colors";
import Dot from "./Dot";
import Video from "./Video";
import Content from "./Content";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    opacity: 0,
    transition: "opacity 1s",
    overflow: "hidden",

    "&>div": {
      maxWidth: "100%",
      maxHeight: "100%",
    },
  },
  visible: {
    opacity: 1,
  },
  tools: {
    position: "absolute",
    left: 0,
    top: 0,
    zIndex: 99,
  },
  wrapper: {
    position: "relative",
    cursor: "move",
    transition: "filter 0.5s",
  },
  img: {
    position: "absolute",
    width: "100%",
    height: "100%",
    left: 0,
    top: 0,
  },
  info: {
    position: "absolute",
    right: 0,
    top: 0,
    background: "#fff",
    zIndex: 99,
  },
  target: {
    position: "absolute",
    zIndex: 99,
    width: 40,
    height: 40,
    borderRadius: 20,
    background: red[500],
    cursor: "pointer",
  },
  blur: {
    filter: "blur(20px)",
  },
  contentWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "auto",
    zIndex: 100,
    display: "none",
  },
  contentWrapperOpen: {
    display: "block",
  },
  boxWrapper: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    [theme.breakpoints.up("sm")]: {
      flexDirection: "column",
      maxWidth: `${theme.breakpoints.width("md")}px !important`,
      marginLeft: "auto",
      marginRight: "auto",
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
    },
  },
  boxVideo: {
    [theme.breakpoints.up("sm")]: {
      width: "100%",
      flexShrink: 0,
      padding: theme.spacing(2),
    },
  },
  boxContent: {
    [theme.breakpoints.up("sm")]: {
      width: "100%",
      flexShrink: 0,
    },
  },
  hint: {
    position: "fixed",
    top: theme.spacing(2),
    left: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 999,
    background: amber[400],
  },
}));

const initialScale = 1;

const Home = () => {
  const classes = useStyles();
  const size = useWindowSize();
  const [imgSize, setImgSize] = useState(null);
  const [imgOriginalSize, setImgOriginalSize] = useState(null);
  const [posX, setPosX] = useState(0);
  const [posY, setPosY] = useState(0);
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [currentContent, setCurrentContent] = useState(null);
  const [showHint, setShowHint] = useState(true);

  useEffect(() => {
    const img = new Image();
    img.onload = function () {
      setImgOriginalSize([this.width, this.height]);
    };
    img.src = WIMMEL;

    fetch(CSV)
      .then((resp) => resp.text())
      .then(function (data) {
        if (data) {
          const rows = data.split(";;;;");
          const c = rows
            .map((r) => {
              const col = r.split(";");
              return {
                name: col[0],
                x: !col[1] ? null : col[1].replace(",", ".") + "%",
                y: !col[2] ? null : col[2].replace(",", ".") + "%",
                yt: col[3],
                text:
                  col[4] && col[4].replace(/"/g, "").replace(/\]\s\(/g, "]("),
                title: col[5],
              };
            })
            .filter((f) => f.x && f.y);
          setContent(c);
        }
      });
  }, []);

  useEffect(() => {
    if (
      !size ||
      size.length !== 2 ||
      !imgOriginalSize ||
      imgOriginalSize.length !== 2
    ) {
      return;
    }

    let newImgW = size[0];
    let newImgH = newImgW * (imgOriginalSize[1] / imgOriginalSize[0]);

    if (newImgH < size[1]) {
      newImgH = size[1];
      newImgW = newImgH * (imgOriginalSize[0] / imgOriginalSize[1]);
    }

    setImgSize([newImgW, newImgH]);
  }, [size, imgOriginalSize]);

  useEffect(() => {
    if (!imgSize || imgSize.length !== 2) {
      return;
    }

    setTimeout(() => {
      setPosY((size[1] - imgSize[1]) / 2);
      setPosX((size[0] - imgSize[0]) / 2);
      setVisible(true);
    }, 100);

    setTimeout(() => {
      setPosY(undefined);
      setPosX(undefined);
    }, 200);
  }, [imgSize]);

  const handleShowDetails = (contentIdx) => {
    setShowHint(false);
    setCurrentContent(content[contentIdx]);
    setOpen(true);
    setTimeout(() => {
      document.getElementById("contentWrapper").scrollTop = 0;
    }, 0);
  };

  const handleHideDetails = () => {
    setCurrentContent(null);
    setOpen(false);
  };

  if (!imgSize || !imgOriginalSize || !content) {
    return null;
  }

  return (
    <div className={clsx(classes.root, visible && classes.visible)}>
      <TransformWrapper
        defaultScale={initialScale}
        pan={{ valocity: true }}
        positionY={posY}
        positionX={posX}
        wheel={{ step: 50 }}
      >
        <React.Fragment>
          <TransformComponent>
            <div
              className={clsx(classes.wrapper, open && classes.blur)}
              style={{ width: imgSize[0], height: imgSize[1] }}
            >
              <img src={WIMMEL} alt="Wimmelbild" className={classes.img} />
              {!open &&
                content.map((c, key) => (
                  <React.Fragment key={key}>
                    <Dot
                      top={c.y}
                      left={c.x}
                      onClick={handleShowDetails}
                      idx={key}
                    />
                  </React.Fragment>
                ))}
            </div>
          </TransformComponent>
        </React.Fragment>
      </TransformWrapper>
      <div
        onClick={handleHideDetails}
        id="contentWrapper"
        className={clsx(
          classes.contentWrapper,
          open && classes.contentWrapperOpen
        )}
      >
        <div className={classes.boxWrapper}>
          <div className={classes.boxVideo}>
            <Zoom in={open}>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Video yt={currentContent?.yt} />
              </div>
            </Zoom>
          </div>
          <div className={classes.boxContent}>
            <Zoom in={open}>
              <Box
                p={2}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Paper>
                  <Box p={4}>
                    <Content
                      title={currentContent?.title}
                      text={currentContent?.text}
                      onClose={handleHideDetails}
                      isMobile={size[0] < 600}
                    />
                  </Box>
                </Paper>
              </Box>
            </Zoom>
          </div>
        </div>
      </div>
      {showHint && (
        <Paper className={classes.hint}>
          <Box p={3}>
            <Typography variant="body1" align="center">
              Klicke einfach auf die <strong>gelben Punkte</strong> und finde
              heraus, was sich hinter den Symbolen verbirgt.
              <Hidden mdDown>
                <>
                  <br />
                  Verschiebe das Bild mit gedrückter Maustaste, um noch mehr zu
                  entdecken.
                </>
              </Hidden>
            </Typography>
          </Box>
        </Paper>
      )}
    </div>
  );
};

export default Home;
